/**
 * MONEY IN — recorded, whatever form it arrived in.
 *
 * This is the answer to the 564. Those jobs are finished with no invoice
 * against them, and Anthony's explanation was "they were billed, they just
 * paid cash and our old system sucked." It had nowhere to put the fact, so
 * hundreds of jobs look unpaid while the money has been in the till for
 * months.
 *
 * Cash is a payment method here, not an exception. So is a check, so is a
 * card the shop ran on its own terminal. The gateway is one way money
 * arrives and the schema does not privilege it.
 *
 * BILLING AND PAYING ARE TWO ACTS and the old system blurred them. An
 * invoice is what the shop asked for; a payment is what turned up. Keeping
 * them apart is what makes "billed and unpaid", "paid in full" and "part
 * paid" three answerable questions instead of one boolean.
 */

import { db } from "./client.ts";
import {
  linesFor, nothingOnIt, numberSuffixFor, invoiceKindFor, billAsTitle, type BillAs,
} from "../domain/billas.ts";
import { paymentLandsOn, finalNet } from "../domain/ppi.ts";
import { ppiFacts, claimPpi } from "./ppi.ts";

export type Method = "cash" | "card" | "check" | "transfer" | "cashapp" | "other";

export const METHOD_LABEL: Record<Method, string> = {
  cash: "Cash",
  card: "Card",
  check: "Check",
  transfer: "Transfer",
  /* Nothing confirms one of these. Cash App has no public API for a
     personal cashtag, so a person watches it arrive and records it, and
     the method exists so the ledger says which ones were taken that way. */
  cashapp: "Cash App",
  other: "Other",
};

export type Payment = {
  id: string; invoice_id: string; amount: number; method: Method;
  reference: string | null; received_at: string;
  /**
   * STRUCK OFF, NOT DELETED. See migration 0092.
   *
   * A shop with a BAR license does not delete money records. A payment
   * entered by mistake stops counting and stays on the page with a name
   * and a reason against it, because "there were two $150 entries and one
   * of them was a mistake" is the answer to a question somebody asks
   * eighteen months later.
   */
  voided_at: string | null;
  void_reason: string | null;
  /**
   * WHO TOOK IT, and the reason a blank one is not a gap.
   *
   * `recordPayment` stamps the signed-in member of staff. The card
   * route does not, because nobody on the desk took that money: the
   * customer keyed their own card on their own page. So null on a card
   * payment MEANS something -- it is how the shop can tell a payment it
   * collected from one the customer made themselves -- and the screen
   * says which rather than leaving the line blank.
   */
  recorded_by: string | null;
  /** Resolved from recorded_by for display; null when nobody keyed it. */
  recordedByName?: string | null;
};

/**
 * A PARTS DEPOSIT, taken before the work on a quote that is not even
 * approved yet. Anthony (D6): "Quotes not yet approved with parts over
 * $100: add ability to record parts deposit payment, reduce total owed at
 * end of job, update customer-facing invoice to reflect deposit, and allow
 * sending invoice to collect the deposit."
 *
 * It is an invoice of its own kind, so a payment still belongs to an
 * invoice and the customer pays it through the same door. The trigger in
 * migration 0075 settles the deposit invoice and leaves the job alone.
 */
export type Deposit = {
  invoiceId: string; invoiceNumber: string; total: number; paid: number;
  status: "open" | "paid" | "void"; payments: Payment[];
};

export type Money = {
  invoiceId: string | null;
  invoiceNumber: string | null;
  /** The final bill, already net of any deposit paid. */
  total: number;
  paid: number;
  balance: number;
  payments: Payment[];
  deposit: Deposit | null;
  /** Deposits actually received on this job, in dollars. */
  depositPaid: number;
};

type Inv = { id: string; invoice_number: string; total: number; kind: string | null; status: "open" | "paid" | "void" };

/** What this job has been billed and what has actually arrived. */
export async function moneyFor(jobId: string, jobTotal: number): Promise<Money> {
  const sb = await db();
  const { data: invs } = await sb.from("invoices")
    .select("id,invoice_number,total,kind,status").eq("job_id", jobId)
    .neq("status", "void")
    .order("issued_at", { ascending: false });
  const all = (invs ?? []) as Inv[];
  const inv = all.find((i) => i.kind !== "deposit");
  const dep = all.find((i) => i.kind === "deposit");

  const ids = [inv?.id, dep?.id].filter(Boolean) as string[];
  /* Voided rows come back so the card can show them struck through, and
     are left out of every total by sum(). One read, both jobs. */
  const { data: pays } = ids.length
    ? await sb.from("payments")
        .select("id,invoice_id,amount,method,reference,received_at,voided_at,void_reason,recorded_by")
        .in("invoice_id", ids).order("received_at", { ascending: false })
    : { data: [] };
  const allPays = (pays ?? []) as Payment[];

  /**
   * PUT A NAME ON THE MONEY.
   *
   * "$147.50, transfer" does not answer who took it or whether the
   * customer paid it themselves, and that is the question asked months
   * later when a figure is disputed. One read for however many people
   * appear on this job, which is almost always one.
   */
  const byIds = [...new Set(allPays.map((p) => p.recorded_by).filter(Boolean))] as string[];
  if (byIds.length) {
    const { data: who } = await sb.from("profiles").select("id,full_name").in("id", byIds);
    const name = new Map(((who ?? []) as Array<{ id: string; full_name: string | null }>)
      .map((w) => [w.id, w.full_name]));
    for (const p of allPays) {
      p.recordedByName = p.recorded_by ? (name.get(p.recorded_by) ?? null) : null;
    }
  }
  const sum = (rows: Payment[]) =>
    rows.filter((p) => !p.voided_at).reduce((n, p) => n + Number(p.amount ?? 0), 0);

  const deposit: Deposit | null = dep ? {
    invoiceId: dep.id, invoiceNumber: dep.invoice_number, total: Number(dep.total ?? 0),
    paid: sum(allPays.filter((p) => p.invoice_id === dep.id)), status: dep.status,
    payments: allPays.filter((p) => p.invoice_id === dep.id),
  } : null;
  const depositPaid = deposit?.paid ?? 0;

  if (!inv) {
    // Not billed yet: what they would owe is the lines less the deposit.
    const owed = Math.round((jobTotal - depositPaid) * 100) / 100;
    return { invoiceId: null, invoiceNumber: null, total: owed, paid: 0, balance: owed, payments: [],
      deposit, depositPaid };
  }
  const payments = allPays.filter((p) => p.invoice_id === inv.id);
  const paid = sum(payments);
  const total = Number(inv.total ?? 0);
  return {
    invoiceId: inv.id, invoiceNumber: inv.invoice_number,
    total, paid, balance: Math.round((total - paid) * 100) / 100, payments,
    deposit, depositPaid,
  };
}

/**
 * IS THIS CAR ALREADY ON A BILL?
 *
 * Shane Lee rang on the Friday night, was quoted $150 then $230 for a
 * tire on his Audi, signed it, and the job was marked done at 10:51 PM
 * with an invoice raised against it. On the Saturday morning the same
 * car was quoted $150 then $230 again, signed again, and actually
 * worked on by a technician. Two records, one tire, and raising the
 * second bill would have asked him for $460.
 *
 * Nothing caught it. `duplicate_jobs` deliberately will not: it offers
 * a one-click retire, so it only ever proposes a copy that is EMPTY --
 * no lines, no invoice, no signature, no photos. Both of these carried
 * all four. That rule is right and must not be loosened; a screen that
 * offers to cancel a signed and photographed job is a worse bug than
 * the one it would catch.
 *
 * So this is the other half: not a retire offer, a WARNING, raised at
 * the moment the second bill would be raised, which is the moment the
 * money actually doubles. It never blocks. A shop really does bill the
 * same car twice in a fortnight -- two repairs, two bills -- and only
 * somebody who was there can tell that from this.
 *
 * SAME CUSTOMER AND SAME CAR, both required. Customer alone cries wolf
 * at anybody running a fleet, and a null vehicle proves nothing, so a
 * job with no car attached raises nothing rather than guessing.
 */
export type NearbyBill = {
  jobId: string; jobNumber: string; invoiceNumber: string;
  total: number; status: string; issuedAt: string;
};

export async function billedElsewhere(jobId: string): Promise<NearbyBill | null> {
  const sb = await db();
  const { data: row } = await sb.from("jobs")
    .select("customer_id,vehicle_id,created_at").eq("id", jobId).maybeSingle();
  const mine = row as
    { customer_id: string | null; vehicle_id: string | null; created_at: string } | null;
  if (!mine?.customer_id || !mine.vehicle_id) return null;

  /**
   * ONLY WHERE THE CLOCK IS A CLOCK.
   *
   * 1,618 of this shop's jobs were written by the import, all carrying
   * the import's timestamp rather than the day the work happened. To
   * a "within a fortnight" test they all look simultaneous, so every
   * car that was ever in twice reads as a double bill: measured on the
   * live book, 80 jobs would have warned. Past the watermark it is 3,
   * and all three are worth a human looking at them.
   *
   * `duplicate_jobs` learned this the same way and guards it the same
   * way. A warning that fires 80 times is one nobody reads.
   */
  const { data: markRow } = await sb.rpc("import_watermark");
  const mark = Date.parse(String(markRow ?? ""));
  const mineAt = Date.parse(mine.created_at);
  if (!Number.isNaN(mark) && mineAt <= mark) return null;

  const { data: sibs } = await sb.from("jobs")
    .select("id,job_number,created_at")
    .eq("customer_id", mine.customer_id)
    .eq("vehicle_id", mine.vehicle_id)
    .neq("id", jobId)
    .neq("status", "cancelled");
  const others = (sibs ?? []) as Array<{ id: string; job_number: string; created_at: string }>;

  /* A fortnight. Long enough to catch a job rebooked to the next day,
     short enough that a return visit months later is not flagged. */
  const FORTNIGHT = 14 * 86400 * 1000;
  const near = others.filter((o) => {
    const at = Date.parse(o.created_at);
    if (!Number.isNaN(mark) && at <= mark) return false;
    return Math.abs(at - mineAt) < FORTNIGHT;
  });
  if (!near.length) return null;

  /* Voided invoices are not bills. A pair already sorted out this way
     must not keep warning about itself for ever. */
  const { data: invs } = await sb.from("invoices")
    .select("id,job_id,invoice_number,total,status,issued_at")
    .in("job_id", near.map((n) => n.id))
    .neq("status", "void")
    .order("issued_at", { ascending: false }).limit(1);
  const inv = ((invs ?? []) as Array<{
    job_id: string; invoice_number: string; total: number; status: string; issued_at: string;
  }>)[0];
  if (!inv) return null;

  const on = near.find((n) => n.id === inv.job_id);
  return {
    jobId: inv.job_id, jobNumber: on?.job_number ?? "another job",
    invoiceNumber: inv.invoice_number, total: Number(inv.total ?? 0),
    status: inv.status, issuedAt: inv.issued_at,
  };
}

/** Deposits received on this job, in dollars. */
async function depositsPaidOn(jobId: string): Promise<number> {
  const sb = await db();
  const { data: deps } = await sb.from("invoices").select("id").eq("job_id", jobId).eq("kind", "deposit");
  const ids = ((deps ?? []) as Array<{ id: string }>).map((d) => d.id);
  if (!ids.length) return 0;
  // Voided money is not money (0092). A struck-off prepayment must not
  // come off the final bill as if it had been paid.
  const { data: pays } = await sb.from("payments").select("amount").in("invoice_id", ids).is("voided_at", null);
  return ((pays ?? []) as Array<{ amount: number }>).reduce((n, p) => n + Number(p.amount ?? 0), 0);
}

/**
 * ASK FOR A DEPOSIT. One per job; asking again reuses the open one. The
 * amount is whatever the shop decides, usually the parts, and the invoice
 * says so in its number so it is never mistaken for the bill.
 */
export async function raiseDepositInvoice(jobId: string, amount: number): Promise<string> {
  if (!(amount > 0)) throw new Error("How much is the deposit?");
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Your login is not attached to a shop.");

  const { data: have } = await sb.from("invoices").select("id,status")
    .eq("job_id", jobId).eq("kind", "deposit").neq("status", "void").limit(1);
  const open = ((have ?? []) as Array<{ id: string; status: string }>)[0];
  if (open) return open.id;

  const { data: job } = await sb.from("jobs")
    .select("job_number,customer_id").eq("id", jobId).maybeSingle();
  const j = job as { job_number: string; customer_id: string | null } | null;
  const total = Math.round(amount * 100) / 100;
  const { data, error } = await sb.from("invoices").insert({
    account_id: accountId, job_id: jobId, customer_id: j?.customer_id ?? null,
    invoice_number: `${j?.job_number ?? "INV"}-D`, kind: "deposit",
    status: "open", subtotal: total, tax: 0, total,
  }).select("id").single();
  if (error) throw new Error(error.message);
  /* WHO ASKED, AND FROM WHICH DESK. A pre-purchase inspection's money is a
     prepayment, and it is credited to whoever raised it: the MechanicRank
     desk writes the same row with via 'mechanicrank-desk'. */
  const ppi = (await ppiFacts(jobId))?.ppi ?? false;
  await sb.from("job_events").insert({
    account_id: accountId, job_id: jobId, kind: "deposit_requested", actor: "staff",
    actor_id: me.user?.id ?? null,
    title: `${ppi ? "Prepayment" : "Parts deposit"} of $${total.toFixed(2)} requested`,
    detail: ppi ? "Paid before the inspection. It comes off the final bill." : "Comes off the final bill.",
    visible_to_customer: true, meta: { amount: total, via: "shop-desk", ...(ppi ? { ppi: true } : {}) },
  });
  if (ppi) await claimPpi(jobId, "raised the prepayment");
  return (data as { id: string }).id;
}

/**
 * Raise the bill. Separate from taking the money, deliberately.
 *
 * The number comes from the job's own lines rather than from anything the
 * browser posted. A total that can be typed is a total that can be typed
 * wrong, and this one is derivable, so it is derived.
 */
/**
 * CHANGE WHAT WAS BILLED.
 *
 * Anthony added a line to job 1611 after the invoice had gone out. Nothing
 * recalculated the total, so the customer's page said $950.15 while the
 * lines added to $1,100.15 -- and correcting it took somebody running SQL
 * against production. A number the shop bills on that only an engineer can
 * change is not a number the shop owns.
 *
 * A PAID INVOICE IS REFUSED. That is settled money: changing the total
 * after the fact makes the ledger disagree with what was actually taken,
 * and the right move on a wrong paid invoice is a refund or a new invoice,
 * both of which leave a trail.
 */
export async function setInvoiceTotal(invoiceId: string, total: number): Promise<void> {
  if (!Number.isFinite(total) || total < 0) throw new Error("That is not an amount.");
  const sb = await db();

  const { data: now, error: readErr } = await sb.from("invoices")
    .select("status").eq("id", invoiceId).maybeSingle();
  if (readErr) throw new Error(readErr.message);
  if ((now as { status?: string } | null)?.status === "paid") {
    throw new Error("That invoice is paid. Refund it or raise a new one instead.");
  }

  const { error } = await sb.from("invoices")
    .update({ total: Math.round(total * 100) / 100 }).eq("id", invoiceId);
  if (error) throw new Error(error.message);
}

/**
 * RAISE ONE DOCUMENT, FOR ONE KIND OF CHARGE.
 *
 * `paper` decides which lines are on it. It defaults to "repair", so every
 * existing caller keeps billing the repair work and nothing else -- which
 * is the change: storage and towing used to be swept onto the repair bill
 * because this summed every line on the job. BAR wants them on their own
 * document with their own authorization, and a storage charge rides on the
 * Civil Code 3068 lien rather than on the repair contract. See 0118.
 *
 * A DEPOSIT ONLY COMES OFF THE REPAIR BILL. A parts deposit was taken
 * against parts. Crediting it against a storage invoice would hand back
 * money on a document it was never paid toward, and leave the repair bill
 * overstated by the same amount.
 *
 * ONLY THE REPAIR BILL MOVES THE JOB TO `invoiced`. Billing somebody for
 * storage says nothing about whether the work is done, and a job that
 * jumped to invoiced because the yard sent a storage bill would read as
 * finished on every board in the building.
 */
export async function raiseInvoice(jobId: string, paper: BillAs = "repair"): Promise<string> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Your login is not attached to a shop.");

  const { data: all } = await sb.from("line_items")
    .select("total,bill_as").eq("job_id", jobId);
  const mine = linesFor((all ?? []) as Array<{ total: number; bill_as: string | null }>, paper);
  const subtotal = mine.reduce((n, l) => n + Number(l.total ?? 0), 0);
  if (!(subtotal > 0)) throw new Error(nothingOnIt(paper));

  const { data: job } = await sb.from("jobs")
    .select("job_number,customer_id").eq("id", jobId).maybeSingle();
  const j = job as { job_number: string; customer_id: string | null } | null;

  // A deposit already taken comes off the bill. "Reduce total owed at end
  // of job" is exactly this line, and the invoice carries the figure so
  // the customer's copy can show where it went.
  const net = finalNet(subtotal, paper === "repair" ? await depositsPaidOn(jobId) : 0);
  const { total, depositApplied } = net;

  const { data, error } = await sb.from("invoices").insert({
    account_id: accountId, job_id: jobId, customer_id: j?.customer_id ?? null,
    invoice_number: `${j?.job_number ?? "INV"}${numberSuffixFor(paper)}`,
    kind: invoiceKindFor(paper),
    status: "open", subtotal, tax: 0, total, deposit_applied: depositApplied,
  }).select("id").single();
  if (error) {
    throw new Error(error.code === "23505"
      ? `This job already has a ${billAsTitle(paper).toLowerCase()} bill with that number.`
      : error.message);
  }
  if (paper === "repair") {
    await sb.from("jobs").update({ status: "invoiced" }).eq("id", jobId);
  }
  /**
   * PAID AHEAD IN FULL, SO THE BILL IS $0 AND THE JOB IS DONE.
   *
   * A prepaid inspection reaches here with the prepayment covering every
   * line. The trigger that closes a settled job fires on a PAYMENT, and a
   * $0 bill never gets one, so without this the job sat at "invoiced, $0
   * owing" for ever. Migration 0204 makes the database do the same on
   * insert; this is the half that works before it lands.
   */
  if (paper === "repair" && net.settled) {
    const id = (data as { id: string }).id;
    await sb.from("invoices").update({ status: "paid" }).eq("id", id);
    const { data: was } = await sb.from("jobs").select("completed_at").eq("id", jobId).maybeSingle();
    await sb.from("jobs").update({
      status: "paid",
      completed_at: (was as { completed_at: string | null } | null)?.completed_at ?? new Date().toISOString(),
    }).eq("id", jobId).neq("status", "cancelled");
  }
  return (data as { id: string }).id;
}

/**
 * Raise the final bill and move the job to `invoiced` -- unless the bill
 * came out at $0 because it was paid ahead, in which case raiseInvoice has
 * already closed it as paid and moving it back to invoiced would undo
 * that. True when the job is now paid.
 */
export async function closedByPrepayment(jobId: string): Promise<boolean> {
  const sb = await db();
  const { data } = await sb.from("jobs").select("status").eq("id", jobId).maybeSingle();
  return (data as { status: string } | null)?.status === "paid";
}

/**
 * Money arrived. Raises the invoice first if there is not one, because
 * "they paid cash for a job nobody ever billed" is the single most common
 * shape in the imported data and asking somebody to do two steps for it
 * is how it goes unrecorded a second time.
 */
export async function recordPayment(
  jobId: string, amount: number, method: Method, reference?: string,
  /** A particular invoice, e.g. the deposit. Otherwise the final bill, raised if needed. */
  invoiceIdWanted?: string,
): Promise<void> {
  if (!(amount > 0)) throw new Error("How much did they pay?");
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Your login is not attached to a shop.");

  // Where the job stood before this payment, so a deposit that wrongly
  // flips it to 'paid' (old trigger) can be put straight back.
  const { data: jobBefore } = await sb.from("jobs")
    .select("status,completed_at").eq("id", jobId).maybeSingle();
  const wasJob = jobBefore as { status: string; completed_at: string | null } | null;

  let invoiceId = invoiceIdWanted;
  let isDeposit = false;
  if (invoiceId) {
    const { data: row } = await sb.from("invoices").select("id,kind,status").eq("id", invoiceId).maybeSingle();
    const r = row as { id: string; kind: string | null; status: string } | null;
    if (!r) throw new Error("That invoice is not on this job.");
    if (r.status === "paid") throw new Error("That invoice is already paid.");
    isDeposit = r.kind === "deposit";
  } else {
    // The final bill only. A deposit invoice is never the one a stray
    // payment lands on.
    const { data: invs } = await sb.from("invoices")
      .select("id,total").eq("job_id", jobId).neq("kind", "deposit").neq("status", "void")
      .order("issued_at", { ascending: false }).limit(1);
    invoiceId = ((invs ?? [])[0] as { id: string } | undefined)?.id;
    if (!invoiceId) {
      // No bill yet. A payment that covers the whole job is settling a
      // completed job, so raise the final bill. A payment SHORT of it is a
      // deposit -- take it as one, so a part payment never raises a full
      // invoice or marks an unbilled job 'invoiced' (job #6: a $300 deposit
      // had raised a $2,700 final bill and advanced the job).
      //
      // AND A PRE-PURCHASE INSPECTION NOT YET DONE IS A PREPAYMENT, however
      // much of it there is. The whole $225 by Zelle before the inspection
      // used to raise the final bill and close the job, which is why staff
      // were marking inspections Done before they happened. It goes on the
      // deposit invoice now, and the job stays open (src/domain/ppi.ts).
      const { data: lines } = await sb.from("line_items").select("total").eq("job_id", jobId);
      const jobTotal = ((lines ?? []) as Array<{ total: number }>)
        .reduce((s, l) => s + Number(l.total ?? 0), 0);
      const facts = await ppiFacts(jobId);
      const lands = paymentLandsOn({
        ppi: facts?.ppi ?? false, status: facts?.status ?? wasJob?.status ?? "",
        hasFinal: false, amount, jobTotal,
      });
      if (lands === "deposit") {
        // A prepayment is asked for at the inspection's price; a part
        // payment on anything else at what was paid, as before.
        const ask = facts?.ppi && jobTotal > 0 ? Math.max(jobTotal, amount) : amount;
        invoiceId = await raiseDepositInvoice(jobId, ask);
        isDeposit = true;
      } else {
        invoiceId = await raiseInvoice(jobId);
      }
    }
  }

  const { error } = await sb.from("payments").insert({
    account_id: accountId, invoice_id: invoiceId,
    amount, method, reference: reference?.trim() || null,
    recorded_by: me.user?.id ?? null,
  });
  if (error) throw new Error(error.message);

  /* ON THE JOB'S HISTORY, WITH A NAME AND A DESK. The payment row already
     carries recorded_by; this is the line the advisor reads, and the one
     the PPI-by-staff count reads alongside the claim. */
  await sb.from("job_events").insert({
    account_id: accountId, job_id: jobId, kind: "payment", actor: "staff",
    actor_id: me.user?.id ?? null,
    title: `$${amount.toFixed(2)} ${method} received${isDeposit ? " ahead of the work" : ""}`,
    visible_to_customer: false,
    meta: { amount, method, invoice_id: invoiceId, via: "shop-desk", ...(isDeposit ? { prepayment: true } : {}) },
  });
  await claimPpi(jobId, isDeposit ? "recorded the prepayment" : "recorded a payment");

  // A deposit settles itself and nothing else. The trigger marks it paid;
  // it must NOT close the job. Migration 0101 teaches the trigger that, but
  // until it lands we undo a wrongful close here -- a no-op once it is live.
  if (isDeposit) {
    if (wasJob && wasJob.status !== "paid") {
      const { data: after } = await sb.from("jobs").select("status").eq("id", jobId).maybeSingle();
      if ((after as { status: string } | null)?.status === "paid") {
        await sb.from("jobs").update({ status: wasJob.status, completed_at: wasJob.completed_at }).eq("id", jobId);
      }
    }
    return;
  }

  // Settled in full closes both the invoice and the job. Anything short of
  // that leaves them open, because a part payment is not a paid job.
  const m = await moneyFor(jobId, 0);
  if (m.balance <= 0.005) {
    await sb.from("invoices").update({ status: "paid" }).eq("id", invoiceId);
    await sb.from("jobs").update({ status: "paid" }).eq("id", jobId);
  }
}

/**
 * A JOB THAT WAS DONE AND PAID AND NEVER WRITTEN DOWN.
 *
 * Anthony, about nine jobs still sitting at Booked with their day gone:
 * "we did all of these and both billed and they paid."
 *
 * The work happened, the money came in, and nothing reached the system —
 * no line, no invoice, no payment, and the status never moved. Six of the
 * nine carry no priced line at all, so there is nothing to invoice from
 * and no honest way to guess the figure. The shop knows it; this writes it
 * down.
 *
 * ORDER MATTERS AND SO DOES WHAT IT REFUSES TO INVENT.
 *
 *   1. A line, only when there is none, described as what it plainly is.
 *      An invoice with no lines behind it is a number with no story.
 *   2. The invoice, at the figure a person typed.
 *   3. The payment. The trigger from migration 0024 then closes both the
 *      invoice and the job and stamps the finish, which is exactly the
 *      path a normal job takes — this does not set a status by hand.
 *
 * It is deliberately not clever. Every number in here came from somebody
 * who was there.
 */
export async function settleJob(
  jobId: string, amount: number, method: string,
): Promise<void> {
  if (!(amount > 0)) throw new Error("How much did it come to?");
  const sb = await db();

  const { data: me } = await sb.auth.getUser();
  const { data: prof } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const accountId = (prof as { account_id: string } | null)?.account_id;
  if (!accountId) throw new Error("Your login is not attached to a shop.");

  // A line, if this job never got one. Without it the invoice says a
  // number and the customer's page says nothing.
  const { data: has } = await sb.from("line_items")
    .select("id").eq("job_id", jobId).limit(1);
  if (!((has ?? []) as unknown[]).length) {
    const { error } = await sb.from("line_items").insert({
      account_id: accountId, job_id: jobId, kind: "labour",
      description: "Work done", quantity: 1, unit_price: amount, position: 0,
    });
    if (error) throw new Error(`Could not put a line on it: ${error.message}`);
  }

  // recordPayment raises the invoice when there is none, and the trigger
  // closes the job the moment the invoice is settled in full.
  await recordPayment(jobId, amount, method as never,
    "Settled from the schedule. The work was done and paid before it was "
    + "written down here.");
}

/**
 * STRIKE A PAYMENT OFF, WITH A REASON.
 *
 * Anthony, 2026-09-09: "now the job 1645 has 150 recorded twice and the
 * customer only paid 150 total."
 *
 * Two people recorded the same $150 fifty-seven minutes apart, and until
 * migration 0092 there was no way for anybody -- the app, the Text Desk or
 * a person -- to take one off. The trigger that settles an invoice only
 * ever fired on INSERT, so money could go up and never down.
 *
 * The reason is required and it is not a formality. "Voided" with no
 * sentence is the row somebody finds in a year and cannot use, and this is
 * the record a shop is asked to produce when a customer says they paid.
 *
 * The invoice and the job put themselves right: the trigger reconciles on
 * this update, so a job wrongly marked paid goes back to invoiced on its
 * own rather than needing a second correction.
 */
export async function voidPayment(paymentId: string, reason: string): Promise<void> {
  const why = reason.trim();
  if (why.length < 4) throw new Error("Say why it is being taken off.");
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { error } = await sb.from("payments").update({
    voided_at: new Date().toISOString(),
    voided_by: me.user?.id ?? null,
    void_reason: why.slice(0, 400),
  }).eq("id", paymentId).is("voided_at", null);
  if (error) throw new Error(error.message);
}
